import { IconName } from "lucide-react/dynamic";

export interface Tag {
  uid: string;
  created_at: string;
  updated_at: string;
  name?: string;
  notes?: string;
  template_id?: number | null;
  shareable: boolean;
  nft_chain_id?: number;
  nft_contract_address?: string;
  nft_token_id?: number;
  verifications: number;
  tag_view_verifications_count: number;
  qc: boolean;
  tamper_enabled?: boolean;
  counter?: number;
  tamper?: string;
  claim_count: number;
  reset_count: number;
  external_verifications: number;
  owner_email?: string;
  owner_address?: string;
  email_marketing_consent?: boolean;
}

export interface SKU {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  description?: string;
  image_url?: string;
  animation_url?: string;
  nft_chain_id?: number;
  nft_contract_address?: string;
  tags_count: number;
  redirect_url?: string;
  notes?: string;
  sku_code?: string;
  upc?: string;
  contract_version?: 0 | 1 | 2 | 3;
  analytics_url?: string;
  tag_verifications_count: number;
  tag_view_verifications_count: number;
  external_tag_verifications_count: number;
  verified_tags_count: number;
  header_logo_url?: string;
  builder_io_api_key?: string;
  builder_io_url?: string;
  email_consent_tags_count: number;
  viewed_tags_count: number;
  owned_tags_count: number;
  claim_count: number;
  reset_count: number;
  page_type: SKUPageType;
  page_content: SKUPageContent;
  unique_content?: SKUUniqueContent | null;
  display_serial_number: boolean;
  available_login_options: LoginOption[];
  login_options: LoginOption[];
}

export enum LoginOption {
  EMAIL = "email",
  GOOGLE = "google",
  APPLE = "apple",
  SMS = "sms",
  BLOCKCHAIN = "blockchain",
}

export const LoginOptionLabels = {
  [LoginOption.EMAIL]: "Email",
  [LoginOption.GOOGLE]: "Google",
  [LoginOption.APPLE]: "Apple",
  [LoginOption.SMS]: "SMS",
  [LoginOption.BLOCKCHAIN]: "Blockchain",
} as const;

export const SKUPageContentType = "lgt_content";

export interface SKUPageContent {
  type?: typeof SKUPageContentType;
  data?: SKUPageContentTab[];
  options?: SKUPageContentOptions;
  version?: "2.0.0" | "1.0.0";
}

export type SKUPageContentGenericTabIcon = IconName | "builder-io";

export type SKUPageContentGenericTabOptions = {
  tab_title?: string;
  tab_icon?: SKUPageContentGenericTabIcon;
};

export type SKUPageContentTab =
  | SKUPageContentProductTab
  | SKUPageContentMusicTab
  | SKUPageContentBuilderIOTab
  | SKUPageContentGalleryTab
  | SKUPageContentUniqueTab
  | SKUPageContentWebpageTab;

export type SKUPageContentTabOptions = SKUPageContentTab["options"];

export type SKUPageContentOptionsSettings = {
  theme: SKUPageContentTheme;
};

export type SKUPageContentOptionsIntegrations = {
  google_analytics: {
    measurement_id: string;
  };
  linkedin_insight: {
    partner_id: string;
  };
  meta_pixel: {
    tracking_id: string;
  };
};

export interface SKUPageContentOptions {
  settings: SKUPageContentOptionsSettings;
  integrations: SKUPageContentOptionsIntegrations;
}

export enum SKUPageContentTheme {
  LIGHT = "light",
  DARK = "dark",
  SYSTEM = "system",
}

export enum SKUPageContentTabType {
  PRODUCT_TAB = "product_tab",
  MUSIC_TAB = "music_tab",
  BUILDER_IO_TAB = "builder_io_tab",
  GALLERY_TAB = "gallery_tab",
  UNIQUE_TAB = "unique_tab",
  WEBPAGE_TAB = "webpage_tab",
}

export interface UniqueTabOptions {
  title: string;
  description: string;
  show_header_title: boolean;
  show_header_description: boolean;
  sync_with_tamper_status: boolean;
}

export enum UniqueTabColumnType {
  VIDEO = "video",
  IMAGE = "image",
  TEXT = "text",
}

export interface UniqueTabData {
  type: "column_mapping";
  version: "1.0.0";
  options: {
    column_name: string;
    column_type: UniqueTabColumnType;
    is_downloadable?: boolean;
  };
}

export interface SKUPageContentProductTab {
  type: SKUPageContentTabType.PRODUCT_TAB;
  version: "1.0.0";
  options: ProductTabOptions;
}

export interface SKUPageContentUniqueTab {
  type: SKUPageContentTabType.UNIQUE_TAB;
  version: "1.0.0";
  options: SKUPageContentGenericTabOptions & UniqueTabOptions;
  data: UniqueTabData[];
}

export interface SKUPageContentBuilderIOTab {
  type: SKUPageContentTabType.BUILDER_IO_TAB;
  version: "1.0.0";
  options: BuilderIOTabOptions;
}

export interface SKUPageContentMusicTab {
  type: SKUPageContentTabType.MUSIC_TAB;
  version: "1.0.0";
  options: MusicTabOptions;
  data: MusicTabData[];
}

export interface SKUPageContentGalleryTab {
  type: SKUPageContentTabType.GALLERY_TAB;
  version: "1.0.0";
  options: GalleryTabOptions;
  data: GalleryTabData[];
}

export interface SKUPageContentWebpageTab {
  type: SKUPageContentTabType.WEBPAGE_TAB;
  version: "1.0.0";
  options: WebpageTabOptions;
}

// TODO: Allow more properties to be injected in the Product Tab Options
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ProductTabOptions extends SKUPageContentGenericTabOptions {}

export interface BuilderIOTabOptions extends SKUPageContentGenericTabOptions {
  builder_io_path: string;
  builder_io_api_key: string;
  sync_with_tamper_status: boolean;
}

export interface MusicTabOptions extends SKUPageContentGenericTabOptions {
  title: string;
  description: string;
  cover_image_url?: string;
  show_header_title: boolean;
  show_header_description: boolean;
  show_audio_artist_name: boolean;
  sync_with_tamper_status: boolean;
  is_downloadable_all: boolean;
}

export interface MusicTabDataOptions {
  track_url: string;
  thumbnail_url: string;
  title: string;
  artist: string;
  isrc?: string;
  is_downloadable?: boolean;
}

export interface MusicTabData {
  type: "track";
  version: "1.0.0";
  options: MusicTabDataOptions;
}

export interface GalleryTabOptions extends SKUPageContentGenericTabOptions {
  title: string;
  description: string;
  show_header_title: boolean;
  show_header_description: boolean;
  show_content_titles: boolean;
  show_content_descriptions: boolean;
  sync_with_tamper_status: boolean;
}

export type GalleryTabData = GalleryTabDataImage | GalleryTabDataVideo;

export interface GalleryTabDataImage {
  type: "image";
  version: "1.0.0";
  options: {
    title: string;
    description: string;
    image_url: string;
    is_downloadable?: boolean;
  };
}

export interface GalleryTabDataVideo {
  type: "video";
  version: "1.0.0";
  options: {
    title: string;
    description: string;
    video_url: string;
    is_downloadable?: boolean;
  };
}

export interface WebpageTabOptions extends SKUPageContentGenericTabOptions {
  webpage_url: string;
  sync_with_tamper_status: boolean;
}

export enum SKUPageType {
  LEGITIMATE = "legitimate",
  REDIRECT = "redirect",
  BUILDER_IO = "builder_io",
}

export interface SKUUniqueContent {
  tag_data_map: Record<string, unknown>;
  metadata: {
    column_names: string[];
    file_name?: string;
  };
}

export enum SKULoginType {
  OFF = 0,
  EMAIL_SOCIAL = 1,
  EMAIL_SOCIAL_WEB3 = 2,
}

export enum TransferType {
  mint = "mint",
  claim = "claim",
  recover = "recover",
}

export enum AnalyticsPeriod {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  HOUR = "hour",
}

export interface TagOwnerChange {
  id: number;
  tag_uid: string;
  sender_address?: string;
  receiver_address?: string;
  created_at: string;
  transfer_type: TransferType;
  nft_token_id: number;
  sender_email?: string;
  receiver_email?: string;
}

export interface GoogleAnalytics {
  views_by_browser: Record<string, number>;
  views_by_device: Record<string, number>;
  views_by_os: Record<string, number>;
  re_engagement_metrics: ReEngagementMetrics;
  session_data: GoogleAnalyticsSessionData;
  total_duration_data: Record<string, number>;
  average_duration_data: Record<string, number>;
}
export interface ReEngagementMetrics {
  total_users_with_claims: number;
  users_with_re_engagement: number;
  re_engagement_rate: number;
  average_verifications_after_claim: number;
  average_views_after_claim: number;
  total_subsequent_verifications: number;
  total_subsequent_views: number;
}

export interface GoogleAnalyticsSessionData {
  total_sessions: number;
  total_duration: number;
  average_duration: number;
}

export interface SKUAnalytics {
  tag_view_verifications_count: number;
  tag_view_verifications_data: Record<string, number>;
  tag_verifications_count: number;
  tag_verifications_data: Record<string, number>;
  external_tag_verifications_count: number;
  external_tag_verifications_data: Record<string, number>;
  tag_program_verifications_count: number;
  tag_program_verifications_data: Record<string, number>;
  tag_custom_verifications_count: number;
  tag_custom_verifications_data: Record<string, number>;
  claim_count: number;
  claim_data: Record<string, number>;
  reset_count: number;
  reset_data: Record<string, number>;
  email_consent_claim_count: number;
  email_consent_claim_data: Record<string, number>;
  views_by_city: Record<string, number>;
  views_by_region: Record<string, number>;
  views_by_country: Record<string, number>;
  views_by_tag: Record<string, number>;
  period: (typeof AnalyticsPeriod)[keyof typeof AnalyticsPeriod];
  latest_verifications: TagVerification[];
  latest_tag_owner_changes: TagOwnerChange[];
  view_lat_long: Array<[number, number]>;
  google_analytics: GoogleAnalytics;
}

export interface TagAnalytics {
  tag_view_verifications_count: number;
  tag_view_verifications_data: Record<string, number>;
  tag_verifications_count: number;
  tag_verifications_data: Record<string, number>;
  external_tag_verifications_count: number;
  external_tag_verifications_data: Record<string, number>;
  tag_program_verifications_count: number;
  tag_program_verifications_data: Record<string, number>;
  tag_custom_verifications_count: number;
  tag_custom_verifications_data: Record<string, number>;
  claim_count: number;
  claim_data: Record<string, number>;
  reset_count: number;
  reset_data: Record<string, number>;
  email_consent_claim_count: number;
  email_consent_claim_data: Record<string, number>;
  views_by_city: Record<string, number>;
  views_by_region: Record<string, number>;
  views_by_country: Record<string, number>;
  period: (typeof AnalyticsPeriod)[keyof typeof AnalyticsPeriod];
  latest_verifications: TagVerification[];
  latest_tag_owner_changes: TagOwnerChange[];
  view_lat_long: Array<[number, number]>;
  google_analytics: GoogleAnalytics;
}

export interface Owner {
  id: number;
  address: string;
  created_at: string;
  updated_at: string;
}

export interface TagVerification {
  created_at: string;
  verify_type: TagVerificationType;
  template_id: number;
  uid: string;
  nft_token_id?: number;
  city?: string;
  region?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  tamper?: string;
  counter: number;
  cmac: string;
  custom_verification_type?: CustomVerificationType;
}

export interface CustomVerificationType {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  tag_verifications_count: number;
}

export interface DeployContractArgs {
  skuId: string;
  body: {
    tokenName: string;
    tokenSymbol: string;
    nftChainId: number;
  };
}

export interface TransferTagsBody {
  fromTemplateId: string;
  toTemplateId: string;
  startingSerial: number;
  endingSerial: number;
}

export enum TagVerificationType {
  ProgrammingQC = "programming_qc",
  UserView = "user_view",
  Custom = "custom",
}

export const TagVerificationTypeLabels = {
  [TagVerificationType.ProgrammingQC]: "Tag Programmed",
  [TagVerificationType.UserView]: "Page View",
  [TagVerificationType.Custom]: "Custom",
} as const;

export const TagVerificationTypes = [
  TagVerificationType.UserView,
  TagVerificationType.ProgrammingQC,
  TagVerificationType.Custom,
] as const;

export interface WorkOSOrganization {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Image {
  id: number;
  cloudflare_image_id: string;
  created_at: string;
  file_name: string;
  url: string;
}

export interface Video {
  id: number;
  created_at: string;
  cloudflare_video_uid: string;
  file_name: string;
  preview_url: string;
  thumbnail_url: string;
  poster_url: string;
  hls_url: string;
  duration_seconds?: number;
  // only for GET /videos/:id to poll processing status
  ready_to_stream?: boolean;
}

export interface Track {
  id: number;
  object_key: string;
  created_at: string;
  file_name: string;
  url: string;
  uid: string;
  file_size_bytes: number;
}

export enum WorkOSPermission {
  CREATE_TAG = "tag:create",
  EXPORT_OWNER = "owners:export",
  VIEW_TAG = "tag:view",
  EDIT_TAG = "tag:edit",
  DELETE_TAG = "tag:delete",
  CREATE_SKU = "sku:create",
  VIEW_SKU = "sku:view",
  EDIT_SKU = "sku:edit",
  DELETE_SKU = "sku:delete",
  VIEW_ANALYTICS = "analytics:view",
  WIDGET_USER_TABLE_MANAGE = "widgets:users-table:manage",
  CREATE_ASSET = "asset:create",
  VIEW_ASSET = "asset:view",
  EDIT_ASSET = "asset:edit",
  DELETE_ASSET = "asset:delete",
  CREATE_CUSTOM_VERIFICATION_TYPE = "custom_verification_type:create",
  VIEW_CUSTOM_VERIFICATION_TYPE = "custom_verification_type:view",
  EDIT_CUSTOM_VERIFICATION_TYPE = "custom_verification_type:edit",
  DELETE_CUSTOM_VERIFICATION_TYPE = "custom_verification_type:delete",
  WIDGET_API_KEYS_MANAGE = "widgets:api-keys:manage",
}

export interface Organization {
  workos_organization_id: string;
  created_at: string;
  updated_at: string;
  name: string;
  login_options: LoginOption[];
  tags_count: number;
  templates_count: number;
  tag_verifications_count: number;
  external_tag_verifications_count: number;
  tag_view_verifications_count: number;
  reset_count: number;
  claim_count: number;
  owned_tags_count: number;
  verified_tags_count: number;
  viewed_tags_count: number;
  email_consent_tags_count: number;
  images_count: number;
  videos_count: number;
  videos_duration_seconds_count: number;
}

export interface WorkOSSession {
  authenticated: boolean;
  session_id: string;
  organization_id: string;
  role: WorkOSSessionRole;
  permissions: WorkOSPermission[];
  feature_flags: string[] | null;
  user: WorkOSUser;
  impersonator: string;
}

export enum WorkOSSessionRole {
  ADMIN = "admin",
  EDITOR = "editor",
  VIEWER = "viewer",
  TAG_PROGRAMMER = "tag-programmer",
}

export interface WorkOSUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  email_verified: boolean;
  profile_picture_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkOSOrganizationMembership {
  id: string;
  organization_id: string;
  user_id: string;
  role: {
    slug: WorkOSSessionRole;
  };
  created_at: string;
  updated_at: string;
  organization_name?: string; // This is only added on our end
}

export interface WorkOSInvitation {
  id: string;
  email: string;
  state: "pending" | "accepted" | "revoked" | "expired";
  accepted_at?: string;
  revoked_at?: string;
  expires_at?: string;
  token: string;
  accept_invitation_url: string;
  organization_id: string;
  inviter_user_id: string;
  created_at: string;
  updated_at: string;
  organization_name?: string; // This is only added on our end
  inviter_email?: string; // This is only added on our end
}

export interface OrganizationSettings {
  login_options: LoginOption[];
}

export const SessionRoleLabels = {
  [WorkOSSessionRole.ADMIN]: "Admin",
  [WorkOSSessionRole.EDITOR]: "Editor",
  [WorkOSSessionRole.VIEWER]: "Viewer",
  [WorkOSSessionRole.TAG_PROGRAMMER]: "Tag Programmer",
} as const;

export interface AuditLogExport {
  id: string;
  state: "ready" | "pending" | "error";
  url: string;
  created_at: string;
  updated_at: string;
}

interface GasLevel {
  suggestedMaxPriorityFeePerGas: string;
  suggestedMaxFeePerGas: string;
  minWaitTimeEstimate: number;
  maxWaitTimeEstimate: number;
}

export interface GasPrice {
  low: GasLevel;
  medium: GasLevel;
  high: GasLevel;
  estimatedBaseFee: string;
  networkCongestion: number;
  latestPriorityFeeRange: string[];
  historicalPriorityFeeRange: string[];
  historicalBaseFeeRange: string[];
  priorityFeeTrend: "up" | "down" | "neutral";
  baseFeeTrend: "up" | "down" | "neutral";
}

export interface TokenPrice {
  symbol: string;
  name: string;
  market_data: {
    current_price: {
      usd: number;
    };
    ath: {
      usd: number;
    };
  };
}

export interface PaginationInfo {
  count: number;
  first_url: string;
  from: number;
  in: number;
  limit: number;
  scaffold_url: string;
  last: number;
  last_url: string;
  next?: number;
  next_url: string;
  page: number;
  page_url: string;
  pages: number;
  prev?: number;
  prev_url: string;
  to: number;
  series: (`${number}` | number | "gap")[];
}

export interface UpdateUniqueContentArgs {
  file: File;
  onUploadProgress: (uploadedBytes: number, totalBytes: number) => void;
}

export type ListQueryArgs = {
  defaultSort?: { column: string; direction: "asc" | "desc" };
};
